import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Hexagon, Activity, Shield, AlertTriangle, ArrowLeft, Server,
} from "lucide-react";
import { validatorsApi } from "../lib/ledgerApi";

const StatusPill = ({ s }) => {
  const map = {
    online: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    offline: "border-muted-foreground/30 bg-muted text-muted-foreground",
    byzantine: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
    syncing: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  };
  return (
    <span
      data-testid={`validator-status-${s}`}
      className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest ${map[s] || "border-border bg-muted"}`}
    >
      {s}
    </span>
  );
};

export default function Validators() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await validatorsApi.list();
        if (alive) setItems(r);
      } catch {} finally { if (alive) setLoading(false); }
    };
    tick();
    const i = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  const onlineCount = items.filter((v) => v.status === "online").length;
  const totalWeight = items.reduce((acc, v) => acc + (v.weight || 0), 0);
  const consensusHealthy = onlineCount * 3 >= totalWeight * 2;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="validators-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
      </Link>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
          / Network · Validators
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <Server className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
          Validator federation
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          Every block produced by NeoPay is signed by ≥⌈2/3⌉ of the active
          validators. Pubkeys are public; private keys live encrypted in the
          network operator's vault. Phase 6 — simulated 3-validator PBFT.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
        <Cell label="Validators" value={items.length || "—"} />
        <Cell label="Online" value={onlineCount} testid="validators-online-count" />
        <Cell label="Total weight" value={totalWeight} />
        <Cell label="Consensus" highlight={consensusHealthy ? "ok" : "bad"} value={consensusHealthy ? "healthy" : "stalled"} testid="consensus-health" />
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((v) => (
          <Link
            key={v.id}
            to={`/validators/${v.id}`}
            data-testid={`validator-card-${v.name}`}
            className="border border-border rounded-sm bg-card p-5 hover:border-foreground transition-colors"
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <Shield className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <div className="text-sm font-medium truncate">{v.name}</div>
              </div>
              <StatusPill s={v.status} />
            </div>
            <div className="font-mono text-[10px] text-muted-foreground truncate mb-3" title={v.pubkey}>
              {v.pubkey?.slice(0, 24)}…
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
              <Cell mini label="Weight" value={v.weight} />
              <Cell mini label="Signed" value={v.blocks_signed_count ?? 0} />
              <Cell mini label="Missed" value={v.blocks_missed_count ?? 0} />
            </div>
            <div className="mt-3 pt-3 border-t border-border text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
              <Activity className="w-3 h-3" strokeWidth={1.5} />
              proposed {v.blocks_proposed_count ?? 0}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Cell({ label, value, testid, mini, highlight }) {
  const hl = highlight === "ok" ? "text-status-ok" : highlight === "bad" ? "text-status-tampered" : "";
  return (
    <div data-testid={testid} className={mini ? "" : "border border-border rounded-sm bg-card px-3 py-2"}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div className={hl}>{value}</div>
    </div>
  );
}
